import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import endPoints from "../../shared/services/endPoints";
import api from "../../shared/services/api";
import { hideLoader, showLoader } from "./loaderSlice";
import { uploadFile } from "../../utils/uploadFiles";
import { deslugify } from "../../utils/commonFunctions";

export const fetchServices = createAsyncThunk(
  "service/fetchServices",
  async () => {
    const response = await api("get", endPoints.SERVICES);
    return response.data;
  }
);

export const createPaymentIntent = createAsyncThunk(
  "service/payment",
  async (amount) => {

    try {
      const response = await api("post", endPoints.CREATE_PAYMENT, {
        amount
      })
      return response.data
    } catch (error) {
      console.error("Payment error:", error);
      throw error;
    }

  }
)

export const fetchStyles = createAsyncThunk("styles/fetchStyles", async () => {
  const response = await api("get", endPoints.STYLES);
  return response.data;
});

export const fetchServiceById = createAsyncThunk(
  "service/fetchServiceById",
  async (id) => {
    const response = await api("get", endPoints.SERVICES + id);
    return response.data;
  }
);

export const fetchServiceBySlug = createAsyncThunk(
  "service/fetchServiceBySlug",
  async (serviceSlug) => {
    const name = deslugify(serviceSlug)
    const response = await api("get", endPoints.SERVICE_BY_SLUG + `?serviceName=${name}`);
    return response.data;
  }
);

export const fetchSubServices = createAsyncThunk(
  "service/fetchSubServices",
  async (id) => {
    // Initialize query parameters
    const queryParams = new URLSearchParams();

    queryParams.append("service", id);

    const response = await api(
      "get",
      endPoints.SERVICES + "?" + queryParams.toString()
    );
    return response.data;
  }
);

export const createOrder = createAsyncThunk(
  "service/createOrder",
  async ({ data, navigate }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      for (let img of data?.images) {
        const image = await uploadFile(img.beforeImage);
        img.beforeImage = image.path;
      }
      const response = await api(
        "post",
        endPoints.CREATE_ORDER + data.id,
        data
      );

      thunkAPI.dispatch(hideLoader());
      if (data.isDraft) {
        navigate("/dashboard/my-orders");
      } else {
        window.location = response.data.paymentIntent.url;
      }
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const servicesSlice = createSlice({
  name: "services",
  initialState: {
    serviceDetails: {},
    services: [],
    subServices: [],
    furnishingStyles: [],
    status: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state, action) => {
        state.status = "loading";
        action.asyncDispatch(showLoader());
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.status = "succeeded";
        action.asyncDispatch(hideLoader());
        state.services = action.payload;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.status = "failed";
        action.asyncDispatch(hideLoader());
        state.error = action.error.message || null;
      })
      .addCase(fetchServiceById.pending, (state, action) => {
        state.status = "loading";
        action.asyncDispatch(showLoader());
      })
      .addCase(fetchServiceById.fulfilled, (state, action) => {
        state.status = "succeeded";
        action.asyncDispatch(hideLoader());
        state.serviceDetails = action.payload;
      })
      .addCase(fetchServiceById.rejected, (state, action) => {
        state.status = "failed";
        action.asyncDispatch(hideLoader());
        state.error = action.error.message || null;
      })
      .addCase(fetchServiceBySlug.pending, (state, action) => {
        state.status = "loading";
        action.asyncDispatch(showLoader());
      })
      .addCase(fetchServiceBySlug.fulfilled, (state, action) => {
        state.status = "succeeded";
        action.asyncDispatch(hideLoader());
        state.serviceDetails = action.payload;
      })
      .addCase(fetchServiceBySlug.rejected, (state, action) => {
        state.status = "failed";
        action.asyncDispatch(hideLoader());
        state.error = action.error.message || null;
      })
      .addCase(fetchStyles.pending, (state, action) => {
        state.status = "loading";
        action.asyncDispatch(showLoader());
      })
      .addCase(fetchStyles.fulfilled, (state, action) => {
        state.status = "succeeded";
        action.asyncDispatch(hideLoader());
        state.furnishingStyles = action.payload;
      })
      .addCase(fetchStyles.rejected, (state, action) => {
        state.status = "failed";
        action.asyncDispatch(hideLoader());
        state.error = action.error.message || null;
      })
      .addCase(fetchSubServices.pending, (state, action) => {
        state.status = "loading";
        action.asyncDispatch(showLoader());
      })
      .addCase(fetchSubServices.fulfilled, (state, action) => {
        state.status = "succeeded";
        action.asyncDispatch(hideLoader());
        state.subServices = action.payload;
      })
      .addCase(fetchSubServices.rejected, (state, action) => {
        state.status = "failed";
        action.asyncDispatch(hideLoader());
        state.error = action.error.message || null;
      });
  },
});

export default servicesSlice.reducer;
